import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

/**
 * The old UI is gone (18 Oct 2026): every old address — a bookmark, a link in
 * an old e-mail, a habit — opens its page in the new UI. Rules are tried in
 * order; a record's own page keeps its number (challan, ticket, PO, customer …).
 * Anything not listed goes home, which opens the person's own page.
 */
const enc = encodeURIComponent;
const RULES = [
  // Order to delivery
  [/^\/sales-pipeline\/delivery-challans\/([^/]+)/, (m) => `/move/challans/${m[1]}`],
  [/^\/sales-pipeline\/delivery-challans/, () => '/move/challans'],
  [/^\/sales-pipeline\/delivery-register/, () => '/move/challans?status=in_transit'],
  [/^\/sales-pipeline\/return-dc\/([^/]+)/, (m) => `/move/return-challans/${m[1]}`],
  [/^\/sales-pipeline\/return-dc/, () => '/move/return-challans'],
  [/^\/sales-pipeline\/quotations\/(?:view\/)?([^/]+)/, (m) => `/sell/quotations/${m[1]}`],
  [/^\/sales-pipeline\/quotations/, () => '/sell/quotations'],
  [/^\/sales-pipeline\/sales-orders[^/]*\/(?:view\/)?([^/]+)/, (m) => `/sell/sales-orders/${m[1]}`],
  [/^\/sales-pipeline\/sales-orders/, () => '/sell/sales-orders'],
  [/^\/sales-pipeline\/demo/, () => '/sell/demo'],
  [/^\/sales-pipeline/, () => '/sell/sales-orders'],
  [/^\/(operation-management|sales-management)\/delivery-challans/, () => '/move/challans'],
  [/^\/(operation-management|sales-management)\/return-dc/, () => '/move/return-challans'],
  [/^\/(operation-management|sales-management)\/quotations/, () => '/sell/quotations'],
  [/^\/(operation-management|sales-management)/, () => '/sell/sales-orders'],
  [/^\/dispatch\/pending-orders/, () => '/move/orders-to-accept'],
  [/^\/delivery-register-management\/technicians/, () => '/control/users'],
  [/^\/delivery-register-management\/bucket-list/, () => '/move/my-deliveries'],
  [/^\/delivery-register-management/, () => '/move/challans?status=in_transit'],
  [/^\/guard\/po-inward/, () => '/move/gate/po-inward'],
  [/^\/guard/, () => '/move/gate'],

  // Leads and customers
  [/^\/lead-crm\/leads\/(\d+)/, (m) => `/sell/leads/${m[1]}`],
  [/^\/lead-crm\/customers\/(\d+)/, (m) => `/sell/customers/${m[1]}`],
  [/^\/lead-crm\/customers/, () => '/sell/customers'],
  [/^\/lead-crm\/follow-ups/, () => '/sell/follow-ups'],
  [/^\/lead-crm/, () => '/sell/leads'],
  [/^\/customer-management\/customers\/(\d+)/, (m) => `/sell/customers/${m[1]}`],
  [/^\/customer-management/, () => '/sell/customers'],
  [/^\/leads\/(\d+)/, (m) => `/sell/leads/${m[1]}`],
  [/^\/(leads|lead-orders)$/, () => '/sell/leads'],
  [/^\/follow-ups$/, () => '/sell/follow-ups'],
  [/^\/(customers|sales|orders)$/, () => '/sell/customers'],
  [/^\/whatsapp-campaigns\/(\d+)/, (m) => `/sell/whatsapp-campaigns/${m[1]}`],
  [/^\/whatsapp-campaigns/, () => '/sell/whatsapp-campaigns'],

  // Production and stock
  [/^\/floor-pipeline\/tickets\/(\d+)/, (m) => `/produce/tickets/${m[1]}`],
  [/^\/tickets\/(\d+)/, (m) => `/produce/tickets/${m[1]}`],
  [/^\/(floor-pipeline|qc-management|qc-orders|tickets)/, () => '/produce/floor'],
  [/^\/inventory-management\/(parts|parts-dashboard|parts-approval)/, () => '/stock/parts'],
  [/^\/inventory-management\/dispatch-chargers/, () => '/move/chargers'],
  [/^\/inventory-management\/part-vendor-repair/, () => '/stock/part-repairs'],
  [/^\/inventory-management\/customer-assets/, () => '/stock/with-customers'],
  [/^\/customer-inventory/, () => '/stock/with-customers'],
  [/^\/(inventory-management|inventory|warehouse)/, () => '/stock/assets'],
  [/^\/parts/, () => '/stock/parts'],
  [/^\/asset-configuration/, () => '/stock/asset-configuration'],
  [/^\/settings\/asset-configuration/, () => '/stock/asset-configuration'],

  // Procurement
  [/^\/vendor-management\/purchase-orders\/(\d+)/, (m) => `/procure/purchase-orders/${m[1]}`],
  [/^\/vendor-management\/spare-parts-po/, () => '/procure/spare-parts-orders'],
  [/^\/vendor-management\/vendor-repair-dc\/([^/]+)/, (m) => `/procure/repairs/${m[1]}`],
  [/^\/vendor-management\/vendor-repair-dc/, () => '/procure/returns?tab=repairs'],
  [/^\/vendor-management\/return-to-vendor\/([^/]+)/, (m) => `/procure/returns/${m[1]}`],
  [/^\/vendor-management\/(return-to-vendor|returns)/, () => '/procure/returns'],
  [/^\/vendor-management\/vendors\/(\d+)/, (m) => `/procure/vendors/${m[1]}`],
  [/^\/vendor-management\/vendors/, () => '/procure/vendors'],
  [/^\/(vendor-management|procurement)/, () => '/procure/purchase-orders'],

  // Support
  [/^\/support\/tickets\/(\d+)/, (m) => `/serve/tickets/${m[1]}`],
  [/^\/support\/(my-tickets|tech-bucket|technician)/, () => '/serve/my-work'],
  [/^\/support-parts/, () => '/serve/parts-desk'],
  [/^\/support/, () => '/serve/queue'],
  [/^\/loan-laptops\/policy/, () => '/serve/loans/policy'],
  [/^\/loan-laptops\/(\d+)/, (m) => `/serve/loans/${m[1]}`],
  [/^\/loan-laptops/, () => '/serve/loans'],

  // Money
  [/^\/customer-billing\/invoices\/(\d+)/, (m) => `/money/invoices/${m[1]}`],
  [/^\/customer-billing\/credit-notes/, () => '/money/credit-notes'],
  [/^\/customer-billing\/payments/, () => '/money/payments'],
  [/^\/customer-billing/, () => '/money/invoices'],
  [/^\/vendor-billing\/payments/, () => '/money/vendor-payments'],
  [/^\/vendor-billing/, () => '/money/vendor-bills'],
  [/^\/finance\/(dc-invoice|sale-invoice-queue|einvoice-queue)/, () => '/money/gst/queue'],
  [/^\/finance/, () => '/home'],

  // Control
  [/^\/settings\/users/, () => '/control/users'],
  [/^\/settings\/(roles|role-reference)/, () => '/control/roles'],
  [/^\/settings\/role-permissions/, () => '/control/role-permissions'],
  [/^\/settings\/user-permissions/, () => '/control/user-permissions'],
  [/^\/settings/, () => '/control/settings'],
  [/^\/teams/, () => '/control/teams'],
  [/^\/reports/, () => '/control/reports'],
  [/^\/dashboard/, () => '/'],
];

const safeDecode = (x) => { try { return decodeURIComponent(x || ''); } catch { return x || ''; } };

export function legacyTarget(pathname) {
  for (const [re, to] of RULES) {
    const m = pathname.match(re);
    if (m) return to(m.map((x, i) => (i === 0 ? x : enc(safeDecode(x)))));
  }
  return '/';
}

export function OldUiRedirect() {
  const loc = useLocation();
  // The new UI lived under /carret until 18 Oct 2026: same page, without the prefix.
  if (/^\/carret(\/|$)/.test(loc.pathname)) {
    return <Navigate to={`${loc.pathname.replace(/^\/carret/, '') || '/'}${loc.search}`} replace />;
  }
  return <Navigate to={legacyTarget(loc.pathname)} replace />;
}

/** Every old address family, each opening the new UI. */
const OLD_PREFIXES = [
  '/carret', '/sales-pipeline', '/operation-management', '/sales-management', '/dispatch', '/delivery-register-management', '/guard',
  '/lead-crm', '/customer-management', '/leads', '/lead-orders', '/follow-ups', '/customers', '/sales', '/orders',
  '/whatsapp-campaigns', '/floor-pipeline', '/tickets', '/qc-management', '/qc-orders', '/inventory-management', '/inventory',
  '/warehouse', '/customer-inventory', '/parts', '/asset-configuration', '/vendor-management', '/procurement', '/support',
  '/support-parts', '/loan-laptops', '/customer-billing', '/vendor-billing', '/finance', '/settings', '/teams', '/reports', '/dashboard',
];
export const legacyRedirectRoutes = OLD_PREFIXES.flatMap((p) => [
  { path: p, element: <OldUiRedirect /> },
  { path: `${p}/*`, element: <OldUiRedirect /> },
]);
